import { ADMIN_PROSPECT_STATUSES } from './prospectsListRequest.js';

export const ADMIN_PROSPECT_TARGETING_SOURCES = Object.freeze(['manual', 'csv_import']);

export const ADMIN_PROSPECT_TARGETING_CONTACT_STATES = Object.freeze([
  'never_contacted',
  'contacted',
]);

export const ADMIN_PROSPECT_TARGETING_UNITS_MIN = 0;
export const ADMIN_PROSPECT_TARGETING_UNITS_MAX = 99999;

export function emptyAdminProspectTargetingForm() {
  return {
    city: '',
    postalCode: '',
    minUnits: '',
    maxUnits: '',
    status: '',
    source: '',
    contactState: '',
  };
}

/**
 * Snapshot targeting for selection / list requests. Never retain form state.
 * @param {Record<string, unknown> | null | undefined} targeting
 * @returns {Record<string, unknown>}
 */
export function cloneAdminProspectTargeting(targeting) {
  if (!targeting || typeof targeting !== 'object' || Array.isArray(targeting)) return {};
  return { ...targeting };
}

export function adminProspectTargetingHasActiveFilters(targeting) {
  const src = targeting && typeof targeting === 'object' && !Array.isArray(targeting) ? targeting : {};
  return Object.keys(src).length > 0;
}

function compactPostal(value) {
  return String(value ?? '')
    .trim()
    .replace(/[\s-]/g, '');
}

/**
 * @param {unknown} raw
 * @returns {{ ok: true, value: string | undefined } | { ok: false, error: 'postal' }}
 */
export function validateAdminProspectPostalInput(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return { ok: true, value: undefined };
  const compact = compactPostal(trimmed);
  if (!/^[A-Za-z0-9]{3}$/.test(compact) && !/^[A-Za-z0-9]{6}$/.test(compact)) {
    return { ok: false, error: 'postal' };
  }
  return { ok: true, value: trimmed };
}

/**
 * @param {unknown} raw
 * @returns {{ ok: true, value: number | undefined } | { ok: false, error: 'unitsInteger' | 'unitsRange' }}
 */
export function validateAdminProspectUnitsInput(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return { ok: true, value: undefined };
  if (!/^\d+$/.test(trimmed)) return { ok: false, error: 'unitsInteger' };
  const n = Number(trimmed);
  if (!Number.isInteger(n)) return { ok: false, error: 'unitsInteger' };
  if (n < ADMIN_PROSPECT_TARGETING_UNITS_MIN || n > ADMIN_PROSPECT_TARGETING_UNITS_MAX) {
    return { ok: false, error: 'unitsRange' };
  }
  return { ok: true, value: n };
}

/**
 * Build a targeting payload from the advanced filter form.
 * Returns only populated fields. Does not encode targeting keys.
 * @param {Record<string, unknown> | null | undefined} form
 * @returns {{ ok: true, targeting: Record<string, unknown> } | { ok: false, errors: Record<string, string> }}
 */
export function buildAdminProspectTargetingFilters(form = {}) {
  const src = form && typeof form === 'object' ? form : {};
  const errors = {};
  const targeting = {};

  const city = String(src.city ?? '').trim();
  if (city) targeting.city = city;

  const postal = validateAdminProspectPostalInput(src.postalCode);
  if (!postal.ok) errors.postalCode = postal.error;
  else if (postal.value) targeting.postalCode = postal.value;

  const minUnits = validateAdminProspectUnitsInput(src.minUnits);
  const maxUnits = validateAdminProspectUnitsInput(src.maxUnits);
  if (!minUnits.ok) errors.minUnits = minUnits.error;
  if (!maxUnits.ok) errors.maxUnits = maxUnits.error;
  if (minUnits.ok && maxUnits.ok && minUnits.value != null && maxUnits.value != null && minUnits.value > maxUnits.value) {
    errors.minUnits = 'unitsOrder';
    errors.maxUnits = 'unitsOrder';
  }
  if (minUnits.ok && minUnits.value != null && !errors.minUnits) targeting.minUnits = minUnits.value;
  if (maxUnits.ok && maxUnits.value != null && !errors.maxUnits) targeting.maxUnits = maxUnits.value;

  const status = String(src.status ?? '').trim();
  if (status && ADMIN_PROSPECT_STATUSES.includes(status)) targeting.status = status;

  const source = String(src.source ?? '').trim();
  if (source && ADMIN_PROSPECT_TARGETING_SOURCES.includes(source)) targeting.source = source;

  const contactState = String(src.contactState ?? '').trim();
  if (contactState && ADMIN_PROSPECT_TARGETING_CONTACT_STATES.includes(contactState)) {
    targeting.contactState = contactState;
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return { ok: true, targeting };
}

export function formatAdminProspectTargetingCount(totalCount, t) {
  if (!Number.isInteger(totalCount) || totalCount < 0) return '';
  if (totalCount === 0) return t('admin.prospection.targeting.count.zero');
  if (totalCount === 1) return t('admin.prospection.targeting.count.one');
  return t('admin.prospection.targeting.count.other').replace('{count}', String(totalCount));
}

export function targetingFilterErrorKey(code) {
  if (code === 'postal') return 'admin.prospection.targeting.error.postal';
  if (code === 'unitsInteger') return 'admin.prospection.targeting.error.unitsInteger';
  if (code === 'unitsRange') return 'admin.prospection.targeting.error.unitsRange';
  if (code === 'unitsOrder') return 'admin.prospection.targeting.error.unitsOrder';
  return 'admin.prospection.error.invalid';
}

/**
 * Compact chip labels for the applied targeting snapshot.
 * @param {Record<string, unknown> | null | undefined} targeting
 * @param {(key: string) => string} t
 * @returns {{ id: string, label: string }[]}
 */
export function describeAdminProspectTargetingChips(targeting, t) {
  const src = targeting && typeof targeting === 'object' ? targeting : {};
  const chips = [];
  if (src.city) chips.push({ id: 'city', label: String(src.city) });
  if (src.postalCode) chips.push({ id: 'postal', label: String(src.postalCode) });
  if (src.minUnits != null && src.maxUnits != null) {
    chips.push({
      id: 'units',
      label: t('admin.prospection.targeting.chip.unitsRange')
        .replace('{min}', String(src.minUnits))
        .replace('{max}', String(src.maxUnits)),
    });
  } else if (src.minUnits != null) {
    chips.push({
      id: 'units',
      label: t('admin.prospection.targeting.chip.unitsMin').replace('{min}', String(src.minUnits)),
    });
  } else if (src.maxUnits != null) {
    chips.push({
      id: 'units',
      label: t('admin.prospection.targeting.chip.unitsMax').replace('{max}', String(src.maxUnits)),
    });
  }
  if (src.status) {
    const key = `admin.prospection.status.${src.status}`;
    const label = t(key);
    chips.push({ id: 'status', label: label === key ? String(src.status) : label });
  }
  if (src.source) {
    const key = `admin.prospection.targeting.source.${src.source}`;
    const label = t(key);
    chips.push({ id: 'source', label: label === key ? String(src.source) : label });
  }
  if (src.contactState) {
    const key = `admin.prospection.targeting.contact.${src.contactState}`;
    const label = t(key);
    chips.push({ id: 'contact', label: label === key ? String(src.contactState) : label });
  }
  return chips;
}

/**
 * Latest-wins gate so an older list response cannot overwrite a newer one.
 */
export function createAdminProspectRequestGate() {
  let latest = 0;
  return {
    next() {
      latest += 1;
      return latest;
    },
    isCurrent(id) {
      return id === latest;
    },
  };
}
